import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import {
  Figure,
  LadderHead,
  LadderRow,
  PageHeader,
  useCodeLabel,
  useFormats,
} from '@/pages/bacen/shared';
import { fetchCrsfnDispositions } from '@/services/bacen';
import type { AnswerOut, CrsfnGroupRow } from '@/services/types';

/**
 * What the CRSFN decided, per party and per item: the disposition mix, the
 * same by appeal type and by year (never by relator), the amounts a ruling
 * left standing, the provisions the dispositives cite, and the reconciliation
 * of the acordao's amount with the ledger's board fine -- counted, never
 * patched.
 *
 * One disposition is one party in one item; the grain is named on every
 * panel and never summed with acordaos or ledger rows.
 */
export function CrsfnDispositionsPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'crsfn', 'dispositions'],
    queryFn: fetchCrsfnDispositions,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        page="bacen:crsfnDispositions"
        exportPath="/api/v1/bacen/export/crsfn/dispositions"
      />

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={{ ...data.mix, value: data.mix.value?.codes ?? null }}
          titleKey="bacen:crsfnDispositions.mix.title"
          helpKey="bacen:crsfnDispositions.mix.help"
          labelPrefix="bacen:codes.disposition."
        />
        <section className="rounded-lg border bg-card p-4" data-mix-shape>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:crsfnDispositions.shape.title')}
          </h2>
          <Answer answer={data.mix} help={t('bacen:crsfnDispositions.shape.help')}>
            {(mix) => (
              <div className="space-y-3">
                <dl className="grid grid-cols-2 gap-3">
                  <Figure
                    label={t('bacen:crsfnDispositions.shape.dispositions')}
                    value={number.format(mix.dispositions)}
                    second={t('bacen:crsfnDispositions.shape.parsed', {
                      n: number.format(mix.parsed),
                    })}
                    testId="dispositions"
                  />
                  <Figure
                    label={t('bacen:crsfnDispositions.shape.acordaos')}
                    value={number.format(mix.acordaos)}
                    second={t('bacen:crsfnDispositions.shape.parties', {
                      n: number.format(mix.parties),
                    })}
                  />
                </dl>
                <ul className="space-y-1 text-sm">
                  {mix.penalty_types.map((p) => (
                    <li
                      key={p.code}
                      className="flex justify-between gap-3"
                      data-penalty-type={p.code}
                    >
                      <span className="text-muted-foreground">{label('penalty', p.code)}</span>
                      <span className="tabular-nums text-foreground">
                        {number.format(p.count)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(p.share)})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="bacen:crsfnDispositions.guide.grain" />
          </div>
        </section>
      </div>

      <GroupPanel answer={data.by_appeal_type} kind="appeal_type" />
      <GroupPanel answer={data.by_year} kind="year" />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-amounts>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:crsfnDispositions.amounts.title')}
          </h2>
          <Answer answer={data.amounts} help={t('bacen:crsfnDispositions.amounts.help')}>
            {(a) => (
              <div className="space-y-2">
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <LadderHead nLabel={t('bacen:penalties.ladders.n')} />
                    <tbody>
                      <LadderRow
                        label={label('amountRole', 'KEPT')}
                        summary={a.kept}
                        currency={currency}
                      />
                      <LadderRow
                        label={label('amountRole', 'REDUCED_TO')}
                        summary={a.reduced_to}
                        currency={currency}
                      />
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground" data-amount-counts>
                  {t('bacen:crsfnDispositions.amounts.counts', {
                    laddered: number.format(a.laddered),
                    set: number.format(a.set_amounts),
                    mentioned: number.format(a.original_mentioned),
                    foreign: number.format(a.foreign_currency),
                    without: number.format(a.without_amount),
                  })}
                </p>
              </div>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-reconciliation>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:crsfnDispositions.reconciliation.title')}
          </h2>
          <Answer
            answer={data.reconciliation}
            help={t('bacen:crsfnDispositions.reconciliation.help')}
          >
            {(r) => (
              <dl className="grid grid-cols-2 gap-3">
                <Figure
                  label={t('bacen:crsfnDispositions.reconciliation.compared')}
                  value={number.format(r.compared)}
                />
                <Figure
                  label={t('bacen:crsfnDispositions.reconciliation.agree')}
                  value={`${number.format(r.agree)} (${percent.format(r.agree_share)})`}
                  testId="agree"
                />
                <Figure
                  label={t('bacen:crsfnDispositions.reconciliation.acordaoLower')}
                  value={number.format(r.acordao_lower)}
                />
                <Figure
                  label={t('bacen:crsfnDispositions.reconciliation.acordaoHigher')}
                  value={number.format(r.acordao_higher)}
                />
              </dl>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="bacen:crsfnDispositions.guide.reconciliation" />
          </div>
        </section>
      </div>

      <section className="rounded-lg border bg-card p-4" data-provisions>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnDispositions.provisions.title')}
        </h2>
        <Answer answer={data.provisions} help={t('bacen:crsfnDispositions.provisions.help')}>
          {(rows) => (
            <div className="max-h-96 overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {t('bacen:crsfnDispositions.provisions.provision')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:crsfnDispositions.provisions.dispositions')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:crsfnDispositions.provisions.acordaos')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.provision} className="border-t" data-provision={r.provision}>
                      <td className="px-3 py-2 text-foreground">{r.provision}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(r.dispositions)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(r.acordaos)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
      </section>
    </div>
  );
}

function GroupPanel({
  answer,
  kind,
}: {
  answer: AnswerOut<CrsfnGroupRow[]>;
  kind: 'appeal_type' | 'year';
}) {
  const { t } = useTranslation(['bacen']);
  const { number, percent } = useFormats();
  const label = useCodeLabel();
  return (
    <section className="rounded-lg border bg-card p-4" data-group-table={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`bacen:crsfnDispositions.groups.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`bacen:crsfnDispositions.groups.${kind}.help`)}>
        {(rows) => (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t(`bacen:crsfnDispositions.groups.${kind}.group`)}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:crsfnDispositions.groups.dispositions')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:crsfnDispositions.groups.negar')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:crsfnDispositions.groups.parcial')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:crsfnDispositions.groups.dar')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:crsfnDispositions.groups.other')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:crsfnDispositions.groups.relief')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.group} className="border-t" data-group={r.group}>
                    <td className="px-3 py-2 text-foreground">
                      {kind === 'appeal_type' ? label('appealType', r.group) : r.group}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(r.dispositions)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(r.negar)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(r.parcial)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(r.dar)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(r.other)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {percent.format(r.relief_share)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Answer>
    </section>
  );
}
